import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * Chrome on iPhone places `position: fixed` against the layout viewport, while
 * getBoundingClientRect() is the visible viewport. The difference hides the
 * top of the drawer under the header and the browser bar. Other browsers
 * report a 0 offset, so this stays a no-op there.
 * @returns {number}
 */
const chromeIosVisualOffset = () => {
  if (!/CriOS/.test(navigator.userAgent)) return 0;

  return window.visualViewport?.offsetTop ?? 0;
};

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  /** @type {number} */
  #openGeneration = 0;

  /**
   * Intentional open state for the main drawer.
   * Do not use only the `open` attribute — close() keeps it until the
   * animation ends, so a second tap would call close again and feel flaky.
   * @type {boolean}
   */
  #drawerOpen = false;

  /** @type {boolean} */
  #drawerTopListenersBound = false;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.#unbindDrawerTopListeners();
  }

  /**
   * Keep the Feestwinkel drawer under the visible top strip (announcement +
   * logo/actions). Measures the top row's viewport bottom so announcement
   * height and sticky scroll position are included.
   */
  #syncFeestwinkelDrawerTop = () => {
    const drawer = this.refs.menuDrawer;
    if (!(drawer instanceof HTMLElement) || !drawer.classList.contains('menu-drawer--feestwinkel')) {
      return;
    }

    const header =
      this.closest('#header-component') ||
      this.closest('header-component') ||
      document.querySelector('#header-component');
    const topRow = header?.querySelector('.header__row--top');
    if (!(topRow instanceof HTMLElement)) return;

    const top = Math.max(0, Math.round(topRow.getBoundingClientRect().bottom + chromeIosVisualOffset()));
    drawer.style.setProperty('--fw-drawer-top', `${top}px`);
  };

  #bindDrawerTopListeners = () => {
    if (this.#drawerTopListenersBound) return;
    this.#drawerTopListenersBound = true;
    window.addEventListener('resize', this.#syncFeestwinkelDrawerTop);
    window.addEventListener('scroll', this.#syncFeestwinkelDrawerTop, { passive: true });
    window.visualViewport?.addEventListener('resize', this.#syncFeestwinkelDrawerTop);
    window.visualViewport?.addEventListener('scroll', this.#syncFeestwinkelDrawerTop);
  };

  #unbindDrawerTopListeners = () => {
    if (!this.#drawerTopListenersBound) return;
    this.#drawerTopListenersBound = false;
    window.removeEventListener('resize', this.#syncFeestwinkelDrawerTop);
    window.removeEventListener('scroll', this.#syncFeestwinkelDrawerTop);
    window.visualViewport?.removeEventListener('resize', this.#syncFeestwinkelDrawerTop);
    window.visualViewport?.removeEventListener('scroll', this.#syncFeestwinkelDrawerTop);
  };

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.#drawerOpen || this.refs.details.classList.contains('menu-open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   * @param {Event} [event]
   */
  toggle(event) {
    // Own the open state — native <details> toggling races with close()'s
    // deferred reset() and makes open/close feel intermittent.
    event?.preventDefault?.();
    event?.stopPropagation?.();
    // Always target the main drawer (do not pass event — nested <details>
    // would make open() attach to the wrong panel).
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    // Submenu summaries also use this path — block native toggle there too.
    event?.preventDefault?.();
    this.#openGeneration += 1;
    if (details === this.refs.details) {
      this.#drawerOpen = true;
    }
    details.setAttribute('open', '');
    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      this.#syncFeestwinkelDrawerTop();
      this.#bindDrawerTopListeners();

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    const generation = ++this.#openGeneration;
    if (details === this.refs.details) {
      this.#drawerOpen = false;
    }
    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');
    if (details === this.refs.details) {
      this.#unbindDrawerTopListeners();
    }

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        // A newer open() call won — do not tear down the drawer
        if (generation !== this.#openGeneration) return;
        reset(details);
        if (details === this.refs.details) {
          this.#drawerOpen = false;
          this.#unbindDrawerTopListeners();
          if (this.refs.menuDrawer instanceof HTMLElement) {
            this.refs.menuDrawer.style.removeProperty('--fw-drawer-top');
          }
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
